import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { IconComponent } from '@cue/ui/icons';
import { cueQrCode, cueStart } from '@cue/ui/icons';
import { translation } from '@cue/translations';
import { TranslocoPipe } from '@jsverse/transloco';
import { ImageDimensionsPipe, LocalizedStringPipe } from '@cue/utilities';
import { QrCodeShowOnFloorplanEnum, ShowOnFloorplanEnum, ShowOnFloorplanWhenActiveRouteEnum } from '@cue/api';
import { NavigationGeodataBM, StartTargetBasicData } from '@cue/data';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { NgStyle, NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'cue-floorplan-button',
  templateUrl: './floorplan-button.component.html',
  styleUrls: ['./floorplan-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TranslocoPipe, ImageDimensionsPipe, LocalizedStringPipe, NgStyle, NgTemplateOutlet],
})
export class FloorplanButtonComponent implements AfterViewInit, OnInit {
  generatedDomId: number;

  isActiveItem = model<boolean>();
  startPointData = input<StartTargetBasicData>();
  targetPointData = input<StartTargetBasicData>();
  floorplanDataItem = input<NavigationGeodataBM>();
  type = input('primary');
  smallResponsive = input(false);
  smallResponsiveAllways = input(false);
  btnClass = input('');
  tempHidePOI = input(false);

  deploymentId = input<string>();
  zoomLevel = model<number>();
  applyTransformOnZoomLevelChange = explicitEffect([this.zoomLevel], () => {
    this.applyTransform();
  });

  customResizeBasedOnZoomEnabled = input<boolean>();
  sizeCoefficient = input<number>();

  protected isDeploymentMode: boolean;
  protected qrCodeShowSettingsForDeploymentMode: QrCodeShowOnFloorplanEnum;
  protected qrCodeShowOnFloorplanEnum = QrCodeShowOnFloorplanEnum;

  protected showOnFloorplanEnum = ShowOnFloorplanEnum;
  protected showOnFloorplanAboveStartTargetEnum = ShowOnFloorplanWhenActiveRouteEnum;
  protected readonly translation = translation;
  protected ICONS = {
    cueQrCode,
    cueStart,
  };

  poiWrapper = viewChild('poiWrapper', { read: ElementRef });

  navigationSettings = inject(NAVIGATION_SETTINGS);

  isVisible = computed(() => {
    const start = this.startPointData();
    if (start) {
      switch (start.showOnFloorplanAbove) {
        case this.showOnFloorplanAboveStartTargetEnum.Icon:
          if (start.icon) return true;
          break;

        case this.showOnFloorplanAboveStartTargetEnum.Image:
          if (start.image) return true;
          break;

        case this.showOnFloorplanAboveStartTargetEnum.Name:
          if (start.name) return true;
          break;
      }
    }

    const target = this.targetPointData();
    if (target) {
      switch (target.showOnFloorplanAbove) {
        case this.showOnFloorplanAboveStartTargetEnum.Icon:
          if (target.icon) return true;
          break;

        case this.showOnFloorplanAboveStartTargetEnum.Image:
          if (target.image) return true;
          break;

        case this.showOnFloorplanAboveStartTargetEnum.Name:
          if (target.name) return true;
          break;
      }
    }

    const item = this.floorplanDataItem();
    if (!item) return false;

    const modeOverride =
      this.isDeploymentMode &&
      this.qrCodeShowSettingsForDeploymentMode !== this.qrCodeShowOnFloorplanEnum.AccordingPoiSettings;

    const showSetting = modeOverride
      ? this.qrCodeShowSettingsForDeploymentMode
      : this.isActiveItem()
        ? item.showOnFloorplanDetail
        : item.showOnFloorplan;

    switch (showSetting) {
      case this.showOnFloorplanEnum.OnlyName:
        return !!(item.mapName || item.name);

      case this.showOnFloorplanEnum.OnlyImage:
      case this.showOnFloorplanEnum.ImageAndName:
        return !!item.linkToImage;

      case this.showOnFloorplanEnum.OnlyCategoryIcon:
        return true;

      case this.showOnFloorplanEnum.CategoryIconAndName:
        return !!(item.mapName || item.name);

      case this.showOnFloorplanEnum.CategoryIconAndImage:
        return !!item.linkToImage;

      case this.showOnFloorplanEnum.OnlyCode:
        return !!item.qrCode;

      case this.showOnFloorplanEnum.CategoryIconAndCode:
        return !!item.qrCode;

      default:
        return false;
    }
  });

  visibilityChanged = output<boolean>();

  emitInvisible = explicitEffect([this.isVisible], ([isVisible]) => {
    this.visibilityChanged.emit(isVisible);
  });

  ngOnInit() {
    this.generatedDomId = Math.floor(Math.random() * (999999 - 100000)) + 100000;

    this.isDeploymentMode =
      (this.navigationSettings.deploymentSettings?.enabled &&
        this.navigationSettings.deploymentSettings.id === this.deploymentId()) ||
      false;
    this.qrCodeShowSettingsForDeploymentMode = this.navigationSettings.deploymentSettings?.qrCodeShowOnFloorplanId;
  }

  convertRgbaToRgb(color?: string, alpha = 1) {
    if (!color || color?.startsWith('rgb(')) {
      return color;
    }
    // Extract first 3 numbers from rgba
    const [r, g, b] = color.match(/\d+/g).map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  ngAfterViewInit(): void {
    this.applyTransform();
  }

  public applyTransform() {
    if (this.poiWrapper() && this.customResizeBasedOnZoomEnabled()) {
      let scaleValue;

      if (this.zoomLevel() >= 0.5) {
        scaleValue = this.sizeCoefficient() * this.zoomLevel();
      } else {
        scaleValue = this.sizeCoefficient() * 0.5;
      }

      this.poiWrapper().nativeElement.style.transform = `scale(${scaleValue})`;
    }
  }

  markItemAsActive() {
    this.isActiveItem.set(true);
  }

  markItemAsInactive() {
    this.isActiveItem.set(false);
  }
}
